import React, { useEffect, useRef } from 'react';
import { Phone, X } from 'lucide-react';
import { FaTelegram, FaWhatsapp } from 'react-icons/fa';
import { gsap } from 'gsap';

interface ContactOptionsModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
}

const contactOptions = [
  {
    label: 'Позвонить',
    value: '+7 (913) 154-43-11',
    href: 'tel:+79131544311',
    icon: Phone,
    accent: 'from-primary-500 to-emerald-500',
  },
  {
    label: 'WhatsApp',
    value: 'Написать в WhatsApp',
    href: 'https://wa.me/79131544311',
    icon: FaWhatsapp,
    accent: 'from-green-500 to-emerald-600',
  },
  {
    label: 'Telegram',
    value: '@ktayfun',
    href: 'https://t.me/ktayfun',
    icon: FaTelegram,
    accent: 'from-sky-500 to-blue-600',
  },
];

const ContactOptionsModal: React.FC<ContactOptionsModalProps> = ({
  isOpen,
  onClose,
  title = 'Свяжитесь с нами',
  subtitle = 'Выберите удобный способ связи. Мы ответим и поможем с выбором продукции.',
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen || !overlayRef.current || !contentRef.current) return;

    gsap.fromTo(overlayRef.current, { opacity: 0 }, { opacity: 1, duration: 0.25, ease: 'power2.out' });
    gsap.fromTo(
      contentRef.current,
      { opacity: 0, y: 26, scale: 0.96 },
      { opacity: 1, y: 0, scale: 1, duration: 0.38, ease: 'back.out(1.5)', delay: 0.05 }
    );
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        ref={contentRef}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
              {title}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 leading-relaxed">
              {subtitle}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Закрыть"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-3">
          {contactOptions.map((option) => {
            const Icon = option.icon as React.ComponentType<{ className?: string }>;

            return (
              <a
                key={option.label}
                href={option.href}
                target={option.href.startsWith('http') ? '_blank' : undefined}
                rel={option.href.startsWith('http') ? 'noreferrer' : undefined}
                className="group flex items-center space-x-4 p-4 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600 bg-gray-50 dark:bg-gray-700/60 hover:bg-white dark:hover:bg-gray-700 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
              >
                <span className={`w-12 h-12 rounded-xl bg-gradient-to-br ${option.accent} flex items-center justify-center text-white shadow-md group-hover:scale-105 transition-transform duration-300`}>
                  <Icon className="w-6 h-6" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-semibold text-gray-900 dark:text-white">
                    {option.label}
                  </span>
                  <span className="block text-sm text-gray-600 dark:text-gray-300 mt-1">
                    {option.value}
                  </span>
                </span>
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ContactOptionsModal;
